import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDownIcon, GripVerticalIcon, PlusIcon, XIcon } from 'lucide-react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Button,
  Combobox,
  EmptyState,
  ErrorNotice,
  Field,
  Panel,
  SelectInput,
  TextInput,
  useDebouncedValue,
  type ComboboxOption,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  listProgramManagers,
  listPrograms,
  saveSystemSettings,
  PROGRAM_MANAGER_CATEGORIES,
  PROGRAM_MANAGER_RULES_SETTING,
  RULE_SCOPES,
  type ProgramManagerContact,
  type ProgramManagerRule,
  type RuleScope,
  type SystemSettings,
} from '@/lib/events';

/** Human labels for each scope, in display order. */
const SCOPE_LABELS: Record<RuleScope, string> = {
  all: 'Any program',
  category: 'A category',
  program: 'A specific program',
};

/** The "leave the program manager blank" choice in the assignee picker. */
const SEE_FACILITATOR_LABEL = 'See Facilitator (no program manager)';

/** A fresh, unsaved rule: broadest scope, no assignee, active. */
function newRule(): ProgramManagerRule {
  return {
    id: crypto.randomUUID(),
    scope: 'all',
    assignContactId: null,
    active: true,
  };
}

/** Whether a rule is complete enough to save. */
function ruleError(rule: ProgramManagerRule): string | null {
  if (rule.scope === 'category' && !rule.category) return 'Choose a category.';
  if (rule.scope === 'program' && !rule.programId) return 'Choose a program.';
  const { minParticipants: min, maxParticipants: max } = rule;
  if (min != null && max != null && min > max) return 'Minimum is above the maximum.';
  return null;
}

/** Parses a participant-count input: blank means unbounded (null). */
function parseCount(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
}

/** A one-line "what this rule does" summary for the collapsed header. */
function ruleSummary(rule: ProgramManagerRule): string {
  const scopePart =
    rule.scope === 'all'
      ? 'Any program'
      : rule.scope === 'category'
        ? (rule.category ?? 'Choose a category')
        : (rule.programName ?? 'Choose a program');
  const { minParticipants: min, maxParticipants: max } = rule;
  const rangePart =
    min == null && max == null
      ? ''
      : ` · ${min ?? 0}–${max ?? '∞'} participants`;
  const assignPart = rule.assignContactId
    ? (rule.assignContactName ?? 'Assigned')
    : 'See Facilitator';
  return `${scopePart}${rangePart} → ${assignPart}`;
}

/**
 * Settings → Program manager rules: the ordered, first-match-wins ruleset that
 * decides which program manager an Opportunity is assigned (BES-34, Phase 2).
 * Rules collapse to named accordion rows; drag the handle to reorder — the
 * order is the precedence, so move the most specific rules above the general
 * ones. Admin-only; the API enforces it. Nothing here writes to a Salesforce
 * Opportunity — that is Phase 3.
 *
 * @param settings - The loaded system settings.
 * @returns The tab.
 */
export function ProgramManagerRulesTab({
  settings,
}: {
  readonly settings: SystemSettings;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const stored = useMemo(
    () => settings[PROGRAM_MANAGER_RULES_SETTING] ?? [],
    [settings],
  );
  const [rules, setRules] = useState<readonly ProgramManagerRule[]>(stored);
  const [saved, setSaved] = useState(false);
  // Rule ids currently expanded. Rules collapse by default (accordion).
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const contacts = useQuery({
    queryKey: ['program-managers'],
    queryFn: ({ signal }) => listProgramManagers(signal),
    staleTime: 5 * 60_000,
  });

  const save = useMutation({
    mutationFn: (next: readonly ProgramManagerRule[]) =>
      saveSystemSettings([{ name: PROGRAM_MANAGER_RULES_SETTING, value: next }]),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      setSaved(true);
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const dirty = JSON.stringify(rules) !== JSON.stringify(stored);
  const errors = rules.map(ruleError);
  const canSave = dirty && errors.every((error) => error === null);

  function update(id: string, patch: Partial<ProgramManagerRule>): void {
    setSaved(false);
    setRules((prev) => prev.map((rule) => (rule.id === id ? { ...rule, ...patch } : rule)));
  }

  function remove(id: string): void {
    setSaved(false);
    setRules((prev) => prev.filter((rule) => rule.id !== id));
  }

  function add(): void {
    setSaved(false);
    const rule = newRule();
    setRules((prev) => [...prev, rule]);
    setExpanded((prev) => new Set(prev).add(rule.id)); // open the new one to edit
  }

  function toggle(id: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSaved(false);
    setRules((prev) => {
      const from = prev.findIndex((rule) => rule.id === active.id);
      const to = prev.findIndex((rule) => rule.id === over.id);
      if (from === -1 || to === -1) return prev;
      return arrayMove([...prev], from, to);
    });
  }

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-2xl text-portal-small text-portal-muted">
          Rules are checked from the top down; the first one that matches an event decides
          its program manager. Drag a rule by its handle to change the order — the order is
          the precedence, so keep the most specific rules above the general ones. Assign a
          person, or choose &ldquo;See Facilitator&rdquo; to leave the program manager blank.
        </p>
        {rules.length > 0 ? (
          <Button variant="secondary" size="compact" onClick={add}>
            <PlusIcon aria-hidden className="size-3.5" /> Add rule
          </Button>
        ) : null}
      </div>

      {rules.length === 0 ? (
        <div className="mt-4">
          <Panel className="p-5">
            <EmptyState
              icon="＋"
              title="No rules yet"
              message="Add a rule to assign program managers automatically. Until then, nothing is assigned by these rules."
              action={
                <Button size="compact" onClick={add}>
                  <PlusIcon aria-hidden className="size-3.5" /> Add rule
                </Button>
              }
            />
          </Panel>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={rules.map((rule) => rule.id)} strategy={verticalListSortingStrategy}>
            <ul className="mt-4 space-y-3">
              {rules.map((rule, index) => (
                <SortableRuleCard
                  key={rule.id}
                  rule={rule}
                  index={index}
                  error={errors[index] ?? null}
                  expanded={expanded.has(rule.id) || (errors[index] ?? null) !== null}
                  contacts={contacts.data ?? []}
                  onToggle={() => toggle(rule.id)}
                  onChange={(patch) => update(rule.id, patch)}
                  onRemove={() => remove(rule.id)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      {contacts.isError ? (
        <div className="mt-4">
          <ErrorNotice
            inline
            title="Could not load program managers"
            message={errorMessage(contacts.error)}
          />
        </div>
      ) : null}

      {save.isError ? (
        <div className="mt-4">
          <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} />
        </div>
      ) : null}

      <div className="mt-4 flex items-center gap-3">
        <Button disabled={save.isPending || !canSave} onClick={() => save.mutate(rules)}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button
          variant="secondary"
          disabled={save.isPending || !dirty}
          onClick={() => {
            setSaved(false);
            setRules(stored);
          }}
        >
          Reset
        </Button>
        {saved && !dirty ? (
          <span className="text-portal-micro font-bold text-emerald-600">Saved</span>
        ) : null}
        {dirty && !canSave ? (
          <span className="text-portal-micro text-portal-muted">
            Complete every rule before saving.
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** One sortable, collapsible rule: a named header over the editable body. */
function SortableRuleCard({
  rule,
  index,
  error,
  expanded,
  contacts,
  onToggle,
  onChange,
  onRemove,
}: {
  readonly rule: ProgramManagerRule;
  readonly index: number;
  readonly error: string | null;
  readonly expanded: boolean;
  readonly contacts: readonly ProgramManagerContact[];
  readonly onToggle: () => void;
  readonly onChange: (patch: Partial<ProgramManagerRule>) => void;
  readonly onRemove: () => void;
}): React.ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rule.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { zIndex: 10, position: 'relative' as const } : {}),
  };
  const idBase = `pm-rule-${rule.id}`;
  const bodyId = `${idBase}-body`;
  const title = rule.name?.trim() || `Rule ${index + 1}`;

  // Show the stored assignee even if they are no longer in the candidate list.
  const contactOptions: ComboboxOption[] = contacts.map((c) => ({ value: c.id, label: c.name }));
  if (rule.assignContactId && !contacts.some((c) => c.id === rule.assignContactId)) {
    contactOptions.unshift({
      value: rule.assignContactId,
      label: rule.assignContactName ?? rule.assignContactId,
    });
  }

  return (
    <li ref={setNodeRef} style={style}>
      <Panel className={`p-0 ${isDragging ? 'shadow-portal-card' : ''} ${rule.active ? '' : 'opacity-60'}`}>
        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="flex items-center gap-2 px-3 py-2.5">
          <button
            type="button"
            aria-label="Drag to reorder"
            className="cursor-grab touch-none rounded-portal-button p-1 text-portal-muted hover:bg-portal-bg hover:text-portal-ink active:cursor-grabbing"
            {...attributes}
            {...listeners}
          >
            <GripVerticalIcon aria-hidden className="size-4" />
          </button>
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-portal-bg text-portal-micro font-bold text-portal-muted">
            {index + 1}
          </span>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-controls={bodyId}
            className="flex min-w-0 flex-1 items-center gap-2 rounded-portal-button py-1 pr-2 text-left hover:text-portal-ink"
          >
            <ChevronDownIcon
              aria-hidden
              className={`size-4 shrink-0 text-portal-muted transition-transform ${expanded ? '' : '-rotate-90'}`}
            />
            <span className="min-w-0">
              <span className="block truncate text-portal-small font-bold text-portal-ink">
                {title}
              </span>
              <span className="block truncate text-portal-micro text-portal-muted">
                {ruleSummary(rule)}
              </span>
            </span>
          </button>
          {error ? (
            <span className="shrink-0 rounded-full bg-rose-50 px-2 py-0.5 text-portal-micro font-bold text-rose-600">
              Incomplete
            </span>
          ) : null}
          <label className="ml-1 flex shrink-0 items-center gap-2 text-portal-small">
            <input
              type="checkbox"
              checked={rule.active}
              onChange={(event) => onChange({ active: event.target.checked })}
              className="size-4 accent-brand"
            />
            Active
          </label>
          <button
            type="button"
            aria-label="Remove rule"
            onClick={onRemove}
            className="shrink-0 rounded-portal-button p-1 text-portal-muted transition-colors hover:bg-portal-bg hover:text-rose-600"
          >
            <XIcon aria-hidden className="size-4" />
          </button>
        </div>

        {/* ── Body ───────────────────────────────────────────────── */}
        {expanded ? (
          <div id={bodyId} className="border-t border-portal-line p-5">
            <div className="grid gap-4 lg:grid-cols-2">
              <Field label="Rule name" htmlFor={`${idBase}-name`} className="lg:col-span-2">
                <TextInput
                  id={`${idBase}-name`}
                  value={rule.name ?? ''}
                  placeholder="e.g. Game shows → Jonathan"
                  onChange={(event) => onChange({ name: event.target.value || undefined })}
                />
              </Field>

              <Field label="Applies to" htmlFor={`${idBase}-scope`}>
                <SelectInput
                  id={`${idBase}-scope`}
                  value={rule.scope}
                  onChange={(event) =>
                    onChange({
                      scope: event.target.value as RuleScope,
                      // Clear the other scope's selection so it can't linger.
                      category: undefined,
                      programId: undefined,
                      programName: undefined,
                    })
                  }
                >
                  {RULE_SCOPES.map((scope) => (
                    <option key={scope} value={scope}>
                      {SCOPE_LABELS[scope]}
                    </option>
                  ))}
                </SelectInput>
              </Field>

              {rule.scope === 'category' ? (
                <Field label="Category" htmlFor={`${idBase}-category`}>
                  <SelectInput
                    id={`${idBase}-category`}
                    value={rule.category ?? ''}
                    onChange={(event) =>
                      onChange({ category: (event.target.value || undefined) as ProgramManagerRule['category'] })
                    }
                  >
                    <option value="">Choose a category…</option>
                    {PROGRAM_MANAGER_CATEGORIES.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </SelectInput>
                </Field>
              ) : null}

              {rule.scope === 'program' ? (
                <Field label="Program" htmlFor={`${idBase}-program`}>
                  <ProgramPicker
                    id={`${idBase}-program`}
                    programId={rule.programId ?? null}
                    programName={rule.programName}
                    onPick={(picked) =>
                      onChange({ programId: picked.id, programName: picked.name })
                    }
                  />
                </Field>
              ) : null}

              <div className="grid grid-cols-2 gap-4 lg:col-span-2">
                <Field
                  label="Minimum participants"
                  htmlFor={`${idBase}-min`}
                  hint="Leave blank for no lower limit."
                >
                  <TextInput
                    id={`${idBase}-min`}
                    type="number"
                    min={0}
                    step={1}
                    value={rule.minParticipants ?? ''}
                    onChange={(event) => onChange({ minParticipants: parseCount(event.target.value) })}
                  />
                </Field>

                <Field
                  label="Maximum participants"
                  htmlFor={`${idBase}-max`}
                  hint="Leave blank for no upper limit."
                >
                  <TextInput
                    id={`${idBase}-max`}
                    type="number"
                    min={0}
                    step={1}
                    value={rule.maxParticipants ?? ''}
                    onChange={(event) => onChange({ maxParticipants: parseCount(event.target.value) })}
                  />
                </Field>
              </div>

              <Field label="Assign to" htmlFor={`${idBase}-assignee`} className="lg:col-span-2">
                <Combobox
                  id={`${idBase}-assignee`}
                  options={contactOptions}
                  value={rule.assignContactId ?? ''}
                  noneLabel={SEE_FACILITATOR_LABEL}
                  placeholder="Search program managers…"
                  onChange={(value) =>
                    onChange({
                      assignContactId: value || null,
                      assignContactName: value
                        ? contactOptions.find((c) => c.value === value)?.label
                        : undefined,
                    })
                  }
                />
              </Field>
            </div>

            {error ? (
              <p className="mt-3 text-portal-micro font-bold text-rose-600">{error}</p>
            ) : null}
          </div>
        ) : null}
      </Panel>
    </li>
  );
}

/**
 * A searchable program picker backed by the programs API. Shows the chosen
 * program with a way to change it; otherwise a debounced name search.
 */
function ProgramPicker({
  id,
  programId,
  programName,
  onPick,
}: {
  readonly id: string;
  readonly programId: string | null;
  readonly programName?: string;
  readonly onPick: (picked: { id: string; name: string }) => void;
}): React.ReactElement {
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query);
  const trimmed = debounced.trim();
  const results = useQuery({
    queryKey: ['programs-search', trimmed],
    queryFn: ({ signal }) => listPrograms(trimmed, signal),
    enabled: trimmed.length >= 2,
    staleTime: 60_000,
  });
  const open = trimmed.length >= 2;
  const programs = (results.data?.programs ?? []).filter((program) => program.salesforceId);

  if (programId) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-portal-button border border-portal-line px-3 py-2">
        <span className="min-w-0 truncate text-portal-small font-bold text-portal-ink">
          {programName ?? programId}
        </span>
        <button
          type="button"
          onClick={() => onPick({ id: '', name: '' })}
          className="shrink-0 text-portal-micro font-bold text-brand hover:underline"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <TextInput
        id={id}
        value={query}
        placeholder="Search programs by name…"
        onChange={(event) => setQuery(event.target.value)}
      />
      {open ? (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-portal-button border border-portal-line bg-white shadow-portal-card">
          {programs.map((program) => (
            <button
              key={program._id}
              type="button"
              onClick={() => {
                onPick({ id: program.salesforceId!, name: program.name ?? '' });
                setQuery('');
              }}
              className="block w-full truncate px-3 py-2 text-left text-portal-small hover:bg-portal-bg"
            >
              {program.name}
            </button>
          ))}
          {results.isPending ? (
            <p className="px-3 py-2 text-portal-micro text-portal-muted">Searching…</p>
          ) : programs.length === 0 ? (
            <p className="px-3 py-2 text-portal-micro text-portal-muted">No programs match.</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
